import { HarborClient, type IApiClient } from "@pharaday/api";
import {
  AuthContainer,
  AuthenticatorProvider,
  decodeJwt,
  InvalidTokenError,
  InvitationToken,
  PasswordResetToken,
} from "@pharaday/auth";
import axios, { type AxiosInstance } from "axios";
import { injectable } from "inversify";

import type { ResetPasswordPayload } from "../../core/application/payload/reset-password.payload";
import type { SignUpPayload } from "../../core/application/payload/sign-up.payload";
import { WeakPasswordError } from "../../core/domain/errors/weak-password.error";
import type { InvitationInfo } from "../../core/domain/invitation-info";
import type { SelfSignUpInvitation } from "../../core/domain/self-sign-up-invitation";
import { OnboardingProvider } from "../application/onboarding.provider";
import { UnauthenticatedContainer } from "../unauthenticated.container";

@injectable()
export class RestOnboardingProvider extends OnboardingProvider {
  public readonly axios: AxiosInstance;

  constructor() {
    super();
    this.axios = UnauthenticatedContainer.get<IApiClient>(HarborClient).getInstance();
  }

  async requestEmailVerification(email: string) {
    await this.axios.post(`auth/signup/request-email-verification`, {
      email,
    });
  }

  async confirmEmailVerification(payload: { email: string; code: string }) {
    const { data } = await this.axios.post<{ activationToken: string }>(`auth/signup/confirm-email-verification`, {
      email: payload.email,
      code: payload.code,
    });
    return data;
  }

  async signUp(payload: SignUpPayload) {
    try {
      await this.axios.post(`auth/signup`, {
        email: payload.email,
        password: payload.password,
        activationToken: payload.activationToken,
      });
    } catch (err) {
      if (
        axios.isAxiosError(err) &&
        err.response?.status === 422 &&
        err.response.data?.cause?.reason === "PasswordFailedValidationError"
      ) {
        throw new WeakPasswordError();
      }
      throw err;
    }
  }

  async getInvitationInfo(token: string) {
    const payload = decodeJwt<InvitationInfo>(token);

    if ((payload?.exp ?? 0) < Date.now() / 1000) {
      throw new InvalidTokenError();
    }

    const { valid } = await AuthContainer.get(AuthenticatorProvider).validateToken(new InvitationToken(token));

    if (!valid) {
      throw new InvalidTokenError();
    }

    return {
      name: payload.name,
      email: payload.email,
    };
  }

  async getSelfSignUpInvitation(token: string) {
    try {
      const { data } = await this.axios.get<SelfSignUpInvitation>(
        `auth/self-signup/invitation/${encodeURIComponent(token)}`
      );
      return data;
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        throw new InvalidTokenError("self-signup invitation");
      }
      throw err;
    }
  }

  async requestSelfSignUpCode(token: string) {
    await this.axios.post(`auth/self-signup/request-code`, {
      token,
    });
  }

  async requestValidationEmailCode(email: string) {
    await this.axios.post(`auth/request/validation-email-code`, {
      email,
    });

    return {
      success: true,
    };
  }

  async validateResetPasswordToken(token: string) {
    const payload = decodeJwt<InvitationInfo>(token);

    if ((payload?.exp ?? 0) < Date.now() / 1000) {
      throw new InvalidTokenError();
    }

    const { valid } = await AuthContainer.get(AuthenticatorProvider).validateToken(new PasswordResetToken(token));

    if (!valid) {
      throw new InvalidTokenError();
    }

    return {
      email: payload.email,
    };
  }

  async requestPasswordReset(email: string) {
    await this.axios.post(`auth/password-reset/request`, {
      email,
    });
  }

  async resetPassword({ token, password }: ResetPasswordPayload) {
    await this.axios.post(`auth/password-reset/${token}`, {
      newPassword: password,
    });
  }
}
